var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import React, { useCallback, useState } from 'react';
import { debounce } from 'lodash';
import { useLocale } from '@adsk/alloy-react-locale';
import { noop } from 'lodash';
import { useFeatureFlag } from '../../LaunchDarklyContext';
import { convertLanLngToString, convertStringToLatLng, } from '../helpers/geoLocationPickerHelpers';
import TextInput from '@adsk/alloy-react-text-input';
import InlineEdit from '@adsk/alloy-react-inline-edit';
import FormField from '@adsk/alloy-react-form-field';
import Button, { IconButton } from '@adsk/alloy-react-button';
import Tooltip from '@adsk/alloy-react-tooltip';
import { PinIcon } from '@adsk/alloy-react-icon';
import MapPreview from './components/MapPreview';
import { LaunchDarklyProviderWrapper } from '../LaunchDarklyProviderWrapper/LaunchDarklyProviderWrapper';
export var latLngRegexExp = /^[+-]?([0-9]|[1-8][0-9]|90)(\.\d+)?, [+-]?(180(\.0+)?|((1[0-7]\d)|([1-9]?\d))(\.\d+)?)$/;
export var GeoLocationPickerComponent = function (_a) {
    var defaultValue = _a.defaultValue, _b = _a.onChange, onChange = _b === void 0 ? noop : _b, _c = _a.onOpenMap, onOpenMap = _c === void 0 ? noop : _c, _d = _a.disabled, disabled = _d === void 0 ? false : _d, _e = _a.disableOpenMap, disableOpenMap = _e === void 0 ? false : _e, _f = _a.isInlineEdit, isInlineEdit = _f === void 0 ? false : _f, customLabel = _a.customLabel, error = _a.error, htmlId = _a.htmlId, placeholder = _a.placeholder;
    var T = useLocale(function (_a) {
        var locale = _a.locale;
        return require("./i18n/".concat(locale, ".json"));
    }).T;
    var enableMapPreview = useFeatureFlag('geo-location-preview').value;
    var _g = __read(useState(function () {
        return convertLanLngToString(defaultValue);
    }), 2), inputValue = _g[0], setInputValue = _g[1];
    var _h = __read(useState(false), 2), isInvalid = _h[0], setIsInvalid = _h[1];
    var _j = __read(useState(false), 2), isInlineFocused = _j[0], setIsInlineFocused = _j[1];
    var _k = __read(useState(false), 2), isTextInputFocused = _k[0], setIsTextInputFocused = _k[1];
    var checkIsValid = function (value) {
        if (!value) {
            return true;
        }
        return latLngRegexExp.test(value);
    };
    var handleInlineChange = useCallback(function (newVal) {
        setInputValue(newVal);
        if (!newVal) {
            onChange(null);
            return;
        }
        var isValueInvalid = !checkIsValid(newVal);
        setIsInvalid(isValueInvalid);
        if (!isValueInvalid) {
            onChange(convertStringToLatLng(newVal));
        }
    }, [onChange]);
    var handleInputChange = useCallback(function (payload) {
        var newVal = payload.target.value;
        setInputValue(newVal);
        var isValueInvalid = !checkIsValid(newVal);
        setIsInvalid(isValueInvalid);
        onChange(convertStringToLatLng(newVal));
    }, [onChange]);
    var handleInlineEditInput = function (ev) {
        var value = ev.target.value;
        setIsInvalid(!checkIsValid(value));
    };
    var handleMapClick = function () {
        if (disabled || disableOpenMap) {
            return;
        }
        onOpenMap();
    };
    var inlinePlaceholder = isInlineFocused
        ? placeholder ||
            T.translate('geoLocationPicker.inputPlaceholder')
        : T.translate('geoLocationPicker.inlinePlaceholder');
    var isEditing = !isInlineEdit || (isInlineEdit && !isInlineFocused);
    var shouldShowMap = enableMapPreview && inputValue && !isInvalid && isEditing;
    var renderIconFunction = function (p) { return React.createElement(PinIcon, __assign({}, p)); };
    return (React.createElement(React.Fragment, null,
        isInlineEdit ? (React.createElement(FormField, { label: customLabel || T.translate('geoLocationPicker.defaultLabel'), labelVariant: "top", error: isInvalid && !isTextInputFocused
                ? T.translate('geoLocationPicker.InlineErrorMsg')
                : '' },
            React.createElement("div", { style: { display: 'flex', alignItems: 'center' } },
                React.createElement(InlineEdit, { style: { marginRight: '6px' }, disabled: disabled, error: isInvalid, placeholder: inlinePlaceholder, value: inputValue, defaultValue: inputValue, onChange: handleInlineChange, onInput: handleInlineEditInput, onEditEnter: function () { return setIsInlineFocused(true); }, onEditExit: function () { return setIsInlineFocused(false); } }),
                !isInlineFocused && !disabled && (React.createElement(Tooltip, { content: T.translate('geoLocationPicker.mapButtonTooltip'), placement: "top", style: { display: 'flex' } },
                    React.createElement(IconButton, { size: 18, renderIcon: renderIconFunction, onClick: onOpenMap, disabled: disableOpenMap, style: { marginTop: '1px' } })))))) : (React.createElement("div", { style: {
                width: '100%',
                display: 'flex',
                gap: '8px',
            } },
            React.createElement(TextInput, { nonce: true, id: htmlId, disabled: disabled, error: error, defaultValue: inputValue, onChange: debounce(handleInputChange, 500), placeholder: placeholder ||
                    T.translate('geoLocationPicker.inputPlaceholder'), onFocus: function () { return setIsTextInputFocused(true); }, onBlur: function () { return setIsTextInputFocused(false); } }),
            !disabled && (React.createElement(Tooltip, { content: T.translate('geoLocationPicker.mapButtonTooltip'), placement: "top" },
                React.createElement(Button, { renderIcon: renderIconFunction, onClick: onOpenMap, disabled: disableOpenMap }))))),
        shouldShowMap && inputValue && checkIsValid(inputValue) && (React.createElement(MapPreview, { onClick: handleMapClick, isClickable: !disabled && !disableOpenMap, lat: +inputValue.split(', ')[0], lng: +inputValue.split(', ')[1] }))));
};
export var GeoLocationPicker = function (args) {
    return (React.createElement(LaunchDarklyProviderWrapper, { projectId: args.projectId },
        React.createElement(GeoLocationPickerComponent, __assign({}, args))));
};
